import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc,
  where,
  addDoc,
  deleteDoc,
  setDoc,
  serverTimestamp,
  getDocs,
  getDoc
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db, functions } from '../firebase';
import CandidateProfile from './CandidateProfile';

const AdminDashboard = () => {
  const [user] = useAuthState(auth);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [filter, setFilter] = useState('all');
  const [phaseFilter, setPhaseFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCohort, setSelectedCohort] = useState('all');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminResult, setAdminResult] = useState(null);
  const [newAdminData, setNewAdminData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: ''
  });
  const [createAdminLoading, setCreateAdminLoading] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [activeTab, setActiveTab] = useState('candidates');
  const [cohorts, setCohorts] = useState([]);
  const [activeCohort, setActiveCohort] = useState(null);
  const [cohortLoading, setCohortLoading] = useState(false);
  const [newCohort, setNewCohort] = useState({
    name: '',
    phase1Start: '',
    phase1Deadline: '',
    phase3Start: '',
    phase3Deadline: '',
    programStart: ''
  });
  const [newSession, setNewSession] = useState({
    date: '',
    time: '',
    zoomLink: ''
  });
  const [editingCohort, setEditingCohort] = useState(null);
  const [editCohortData, setEditCohortData] = useState({});
  const [addingSessionToCohort, setAddingSessionToCohort] = useState(null);
  const [expandedSessions, setExpandedSessions] = useState(new Set());
  const [interviewers, setInterviewers] = useState([]);
  const [newInterviewer, setNewInterviewer] = useState({
    name: '',
    email: '',
    title: '',
    calendarLink: ''
  });

  // Get unified status for display
  const getUnifiedStatus = (app) => {
    const phase = app.currentPhase || 1;
    const status = app.status;
    
    switch (phase) {
      case 1:
        if (status === 'pending') return 'Phase 1 Submitted';
        if (status === 'auto_approved') return 'Phase 1 Approved - Auto';
        if (status === 'approved_phase2') return 'Phase 1 Approved - Manual';
        if (status === 'manual_review') return 'Phase 1 Pending Approval';
        if (status === 'auto_rejected' || status === 'rejected_manual') return 'Phase 1 Failed';
        return 'Phase 1 Submitted';
      
      case 2:
        if (status === 'phase2_invited' || status === 'approved_phase2') return 'Phase 2 Invited';
        return 'Phase 2 In Progress';
      
      case 3:
        if (status === 'phase3_invited') return 'Phase 3 Invited';
        if (status === 'phase3_submitted') return 'Phase 3 Submitted and Pending Approval';
        if (status === 'phase3_pending') return 'Phase 3 Submitted and Pending Approval';
        if (status === 'phase3_approved') return 'Phase 3 Approved - Auto';
        if (status === 'phase3_approved_manual') return 'Phase 3 Approved - Manual';
        if (status === 'phase3_failed') return 'Phase 3 Failed';
        return 'Phase 3 Submitted and Pending Approval';
      
      case 4:
        if (status === 'phase4_invited') return 'Phase 4 Invited';
        if (status === 'phase4_accepted') return 'Phase 4 Accepted';
        if (status === 'phase4_failed') return 'Phase 4 Failed';
        return 'Phase 4 Interview';
      
      default:
        return 'Phase 1 Submitted';
    }
  };

  // Apply filters for CRM view
  const applyFilters = (apps, statusFilter, phaseFilter, searchTerm, cohortFilter) => {
    let filtered = [...apps];
    
    // Apply cohort filter
    if (cohortFilter !== 'all') {
      filtered = filtered.filter(app => app.cohortId === cohortFilter);
    }
    
    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(app => 
        (app.firstName + ' ' + app.lastName).toLowerCase().includes(searchLower) ||
        app.email.toLowerCase().includes(searchLower) ||
        app.companyName?.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply phase filter
    if (phaseFilter !== 'all') {
      switch (phaseFilter) {
        case 'phase1':
          filtered = filtered.filter(app => (app.currentPhase || 1) === 1);
          break;
        case 'phase2':
          filtered = filtered.filter(app => app.currentPhase === 2);
          break;
        case 'phase3':
          filtered = filtered.filter(app => app.currentPhase === 3);
          break;
        case 'phase4':
          filtered = filtered.filter(app => app.currentPhase === 4);
          break;
        case 'accepted':
          filtered = filtered.filter(app => 
            app.status === 'phase4_accepted' || 
            app.status === 'auto_approved' ||
            app.status === 'approved_phase2' ||
            app.status === 'phase3_approved'
          );
          break;
        default:
          break;
      }
    }
    
    // Apply status filter (only for phase 1 and 3)
    if (statusFilter !== 'all' && (phaseFilter === 'phase1' || phaseFilter === 'phase3')) {
      switch (statusFilter) {
        case 'auto_accepted':
          filtered = filtered.filter(app => app.status === 'auto_approved');
          break;
        case 'manually_approved':
          filtered = filtered.filter(app => 
            app.status === 'approved_phase2' || 
            app.status === 'phase3_approved' || 
            app.status === 'phase4_accepted'
          );
          break;
        case 'pending_manual_approval':
          filtered = filtered.filter(app => 
            app.status === 'manual_review' || 
            app.status === 'phase3_pending'
          );
          break;
        case 'denied':
          filtered = filtered.filter(app => 
            app.status === 'auto_rejected' || 
            app.status === 'rejected_manual' ||
            app.status === 'phase3_failed'
          );
          break;
        default:
          break;
      }
    }
    
    return filtered;
  };

  // Load applications data
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    // Load main applications (single source of truth)
    const applicationsQuery = query(collection(db, 'applications'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(applicationsQuery, (snapshot) => {
      const apps = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
      
      // Apply filters
      const filteredApps = applyFilters(apps, filter, phaseFilter, searchTerm, selectedCohort);
      
      setApplications(filteredApps);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, filter, phaseFilter, searchTerm, selectedCohort]);

  // Load cohorts from Firestore
  useEffect(() => {
    if (!user) return;

    const q = query(collection(db, 'cohorts'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const cohortData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
      
      setCohorts(cohortData);
      
      // Set active cohort if one exists
      const active = cohortData.find(c => c.status === 'open_applications');
      if (active && !activeCohort) {
        setActiveCohort(active);
      } else if (!cohortData.find(c => c.status === 'open_applications')) {
        setActiveCohort(null);
      }
    });

    return () => unsubscribe();
  }, [user, activeCohort]);

  // Load interviewers from Firestore
  useEffect(() => {
    if (!user) return;

    const q = query(collection(db, 'interviewers'), orderBy('name', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const interviewerData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
      
      setInterviewers(interviewerData);
    });

    return () => unsubscribe();
  }, [user]);

  // Send approval emails
  const sendApprovalEmails = async () => {
    setEmailLoading(true);
    setEmailResult(null);
    
    try {
      const queuedApps = applications.filter(app => 
        app.emailQueue === true && !app.emailSent
      );
      
      if (queuedApps.length === 0) {
        setEmailResult({
          type: 'success',
          message: 'No candidates in email queue',
          details: { emailsSent: 0, emailsFailed: 0 }
        });
        return;
      }
      
      const sendEmailFunction = httpsCallable(functions, 'sendSingleApprovalEmail');
      
      let successCount = 0;
      let failureCount = 0;
      
      for (const app of queuedApps) {
        try {
          await sendEmailFunction({ applicationId: app.id });
          successCount++;
        } catch (error) {
          console.error(`Failed to send email to ${app.email}:`, error);
          failureCount++;
        }
      }
      
      setEmailResult({
        type: successCount > 0 ? 'success' : 'error',
        message: `Sent ${successCount} approval emails successfully${failureCount > 0 ? `, ${failureCount} failed` : ''}`
      });
      
    } catch (error) {
      console.error('Email processing error:', error);
      setEmailResult({
        type: 'error',
        message: 'Failed to process emails: ' + error.message
      });
    } finally {
      setEmailLoading(false);
    }
  };

  // Send rejection emails to all rejected candidates
  const sendRejectionEmails = async () => {
    setEmailLoading(true);
    setEmailResult(null);
    
    try {
      const sendRejectionEmailsFunction = httpsCallable(functions, 'sendRejectionEmails');
      const result = await sendRejectionEmailsFunction();
      
      setEmailResult({
        type: 'success',
        message: result.data.message,
        details: { emailsSent: result.data.emailsSent || 0 }
      });
      
    } catch (error) {
      console.error('Rejection email processing error:', error);
      setEmailResult({
        type: 'error',
        message: 'Failed to send rejection emails: ' + error.message
      });
    } finally {
      setEmailLoading(false);
    }
  };

  // Send Phase 4 approval emails
  const sendPhase4ApprovalEmails = async () => {
    setEmailLoading(true);
    setEmailResult(null);
    
    try {
      const sendPhase4ApprovalsFunction = httpsCallable(functions, 'sendPhase4ApprovalEmails');
      const result = await sendPhase4ApprovalsFunction();
      
      setEmailResult({
        type: 'success',
        message: result.data.message,
        details: { emailsSent: result.data.emailsSent || 0 }
      });
      
    } catch (error) {
      console.error('Phase 4 approval email processing error:', error);
      setEmailResult({
        type: 'error',
        message: 'Failed to send Phase 4 approval emails: ' + error.message
      });
    } finally {
      setEmailLoading(false);
    }
  };

  // Send Phase 4 rejection emails
  const sendPhase4RejectionEmails = async () => {
    setEmailLoading(true);
    setEmailResult(null);
    
    try {
      const sendPhase4RejectionsFunction = httpsCallable(functions, 'sendPhase4RejectionEmails');
      const result = await sendPhase4RejectionsFunction();
      
      setEmailResult({
        type: 'success',
        message: result.data.message,
        details: { emailsSent: result.data.emailsSent || 0 }
      });
      
    } catch (error) {
      console.error('Phase 4 rejection email processing error:', error);
      setEmailResult({
        type: 'error',
        message: 'Failed to send Phase 4 rejection emails: ' + error.message
      });
    } finally {
      setEmailLoading(false);
    }
  };

  // Create admin user
  const createAdminUser = async () => {
    const { email, password, firstName, lastName } = newAdminData;
    
    if (!email.trim() || !password.trim() || !firstName.trim() || !lastName.trim()) {
      alert('Please fill in all fields');
      return;
    }
    
    if (password.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    
    setCreateAdminLoading(true);
    
    try {
      const createAdmin = httpsCallable(functions, 'createAdminUser');
      const response = await createAdmin({
        email: email.trim(),
        password: password.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim()
      });
      
      setAdminResult({
        type: 'success',
        message: response.data.message
      });
      
      setNewAdminData({
        email: '',
        password: '',
        firstName: '',
        lastName: ''
      });
      
    } catch (error) {
      setAdminResult({
        type: 'error',
        message: 'Failed to create admin user: ' + error.message
      });
    } finally {
      setCreateAdminLoading(false);
    }
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'auto_approved': 'bg-green-100 text-green-800',
      'auto_rejected': 'bg-red-100 text-red-800',
      'manual_review': 'bg-blue-100 text-blue-800',
      'approved_phase2': 'bg-green-100 text-green-800',
      'rejected_manual': 'bg-red-100 text-red-800',
      'phase2_invited': 'bg-purple-100 text-purple-800',
      'phase3_invited': 'bg-purple-100 text-purple-800',
      'phase3_submitted': 'bg-yellow-100 text-yellow-800',
      'phase3_pending': 'bg-blue-100 text-blue-800',
      'phase3_approved': 'bg-green-100 text-green-800',
      'phase3_failed': 'bg-red-100 text-red-800',
      'phase4_invited': 'bg-purple-100 text-purple-800',
      'phase4_accepted': 'bg-green-100 text-green-800',
      'phase4_failed': 'bg-red-100 text-red-800'
    };
    
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Get flag indicators
  const getFlagIndicators = (app) => {
    const indicators = [];
    
    // Phase 1 Flags
    const phase1RedCount = app.redFlags?.length || 0;
    const phase1YellowCount = app.yellowFlags?.length || 0;
    
    // Phase 3 Flags
    const phase3RedCount = app.phase3RedFlags?.length || 0;
    const phase3YellowCount = app.phase3YellowFlags?.length || 0;
    
    // Total flags
    const totalRedFlags = phase1RedCount + phase3RedCount;
    const totalYellowFlags = phase1YellowCount + phase3YellowCount;
    
    // Show Phase 1 flags
    if (phase1RedCount > 0 || phase1YellowCount > 0) {
      indicators.push(
        <div key="phase1" className="flex items-center space-x-1">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            P1: {phase1RedCount > 0 && `${phase1RedCount}R`}{phase1RedCount > 0 && phase1YellowCount > 0 && ' '}{phase1YellowCount > 0 && `${phase1YellowCount}Y`}
          </span>
        </div>
      );
    }
    
    // Show Phase 3 flags (only if candidate is in Phase 3+)
    if (app.currentPhase >= 3 && (phase3RedCount > 0 || phase3YellowCount > 0)) {
      indicators.push(
        <div key="phase3" className="flex items-center space-x-1">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            P3: {phase3RedCount > 0 && `${phase3RedCount}R`}{phase3RedCount > 0 && phase3YellowCount > 0 && ' '}{phase3YellowCount > 0 && `${phase3YellowCount}Y`}
          </span>
        </div>
      );
    }
    
    // Show overall status if no flags
    if (totalRedFlags === 0 && totalYellowFlags === 0) {
      indicators.push(
        <div key="clean" className="flex items-center space-x-1">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Clean
          </span>
        </div>
      );
    }
    
    return indicators;
  };

  // Cohort Management Functions
  const createCohort = async () => {
    if (!newCohort.name || !newCohort.programStart || !newCohort.phase1Start || !newCohort.phase1Deadline || !newCohort.phase3Start || !newCohort.phase3Deadline) {
      alert('Please fill in all required fields');
      return;
    }

    setCohortLoading(true);
    try {
      const cohortData = {
        name: newCohort.name,
        status: 'draft',
        timeline: {
          phase1Start: newCohort.phase1Start,
          phase1Deadline: newCohort.phase1Deadline,
          phase3Start: newCohort.phase3Start,
          phase3Deadline: newCohort.phase3Deadline,
          programStart: newCohort.programStart
        },
        phase2Sessions: [],
        createdAt: new Date(),
        createdBy: user.uid
      };

      await addDoc(collection(db, 'cohorts'), cohortData);
      
      // Reset form
      setNewCohort({
        name: '',
        phase1Start: '',
        phase1Deadline: '',
        phase3Start: '',
        phase3Deadline: '',
        programStart: ''
      });

      alert('Cohort created successfully!');
    } catch (error) {
      console.error('Error creating cohort:', error);
      alert('Failed to create cohort: ' + error.message);
    } finally {
      setCohortLoading(false);
    }
  };

  const saveCohortEdit = async (cohortId) => {
    setCohortLoading(true);
    try {
      const updateData = {
        name: editCohortData.name,
        status: editCohortData.status,
        timeline: editCohortData.timeline,
        updatedAt: new Date()
      };

      console.log('Updating cohort with data:', updateData);

      await updateDoc(doc(db, 'cohorts', cohortId), updateData);
      
      setEditingCohort(null);
      setEditCohortData({});
      alert('Cohort updated successfully!');
    } catch (error) {
      console.error('Error updating cohort:', error);
      alert('Failed to update cohort: ' + error.message);
    } finally {
      setCohortLoading(false);
    }
  };

  const deleteCohort = async (cohortId) => {
    if (!window.confirm('Are you sure you want to delete this cohort? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'cohorts', cohortId));
      alert('Cohort deleted successfully!');
    } catch (error) {
      console.error('Error deleting cohort:', error);
      alert('Failed to delete cohort: ' + error.message);
    }
  };

  const addSession = async (cohortId) => {
    if (!newSession.date || !newSession.time || !newSession.zoomLink) {
      alert('Date, time, and Zoom link are all required');
      return;
    }

    setCohortLoading(true);
    try {
      let sessionCode;
      
      try {
        // Try to generate 8-character verification code via Firebase function
        const generateCode = httpsCallable(functions, 'generatePhase2Code');
        const codeResponse = await generateCode();
        sessionCode = codeResponse.data.code;
        console.log('Generated code via function:', sessionCode);
      } catch (codeError) {
        console.warn('Failed to generate code via function, creating locally:', codeError);
        // Fallback: Generate code locally
        sessionCode = Math.random().toString(36).substring(2, 10).toUpperCase();
      }

      const cohortRef = doc(db, 'cohorts', cohortId);
      const cohortDoc = await getDoc(cohortRef);
      
      if (cohortDoc.exists()) {
        const cohortData = cohortDoc.data();
        const currentSessions = cohortData.phase2Sessions || [];
        
        const sessionData = {
          id: `session_${Date.now()}`,
          date: newSession.date,
          time: newSession.time,
          zoomLink: newSession.zoomLink,
          code: sessionCode,
          createdAt: new Date()
        };

        console.log('Adding session:', sessionData);

        await updateDoc(cohortRef, {
          phase2Sessions: [...currentSessions, sessionData],
          updatedAt: new Date()
        });

        // Reset form and close
        setNewSession({ date: '', time: '', zoomLink: '' });
        setAddingSessionToCohort(null);
        alert(`Session added successfully! Code: ${sessionCode}`);
      }
    } catch (error) {
      console.error('Error adding session:', error);
      alert('Failed to add session: ' + error.message);
    } finally {
      setCohortLoading(false);
    }
  };

  const removeSession = async (cohortId, sessionId) => {
    if (!window.confirm('Are you sure you want to remove this session?')) {
      return;
    }

    try {
      const cohortRef = doc(db, 'cohorts', cohortId);
      const cohortDoc = await getDoc(cohortRef);
      
      if (cohortDoc.exists()) {
        const cohortData = cohortDoc.data();
        const currentSessions = cohortData.phase2Sessions || [];
        const updatedSessions = currentSessions.filter(session => session.id !== sessionId);

        await updateDoc(cohortRef, {
          phase2Sessions: updatedSessions,
          updatedAt: new Date()
        });

        alert('Session removed successfully!');
      }
    } catch (error) {
      console.error('Error removing session:', error);
      alert('Failed to remove session: ' + error.message);
    }
  };

  // Get attendees for a specific session
  const getSessionAttendees = (sessionId, cohortId) => {
    return applications.filter(app => 
      app.cohortId === cohortId && 
      app.selectedSessions?.includes(sessionId) &&
      (app.status === 'approved_phase2' || app.status === 'auto_approved' || app.currentPhase >= 2)
    );
  };

  // Toggle session attendee visibility
  const toggleSessionExpansion = (sessionId) => {
    const newExpanded = new Set(expandedSessions);
    if (newExpanded.has(sessionId)) {
      newExpanded.delete(sessionId);
    } else {
      newExpanded.add(sessionId);
    }
    setExpandedSessions(newExpanded);
  };

  // Add interviewer
  const addInterviewer = async () => {
    if (!newInterviewer.name || !newInterviewer.email) {
      alert('Name and email are required');
      return;
    }

    try {
      await addDoc(collection(db, 'interviewers'), {
        ...newInterviewer,
        createdAt: serverTimestamp(),
        createdBy: user.uid
      });

      setNewInterviewer({
        name: '',
        email: '',
        title: '',
        calendarLink: ''
      });

      alert('Interviewer added successfully!');
    } catch (error) {
      console.error('Error adding interviewer:', error);
      alert('Failed to add interviewer: ' + error.message);
    }
  };

  // Remove interviewer
  const removeInterviewer = async (interviewerId) => {
    if (!confirm('Are you sure you want to remove this interviewer?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'interviewers', interviewerId));
      alert('Interviewer removed successfully!');
    } catch (error) {
      console.error('Error removing interviewer:', error);
      alert('Failed to remove interviewer: ' + error.message);
    }
  };

  // Statistics
  const stats = {
    total: applications.length,
    phase1: applications.filter(app => (app.currentPhase || 1) === 1).length,
    phase2: applications.filter(app => app.currentPhase === 2).length,
    phase3: applications.filter(app => app.currentPhase === 3).length,
    phase4: applications.filter(app => app.currentPhase === 4).length,
    accepted: applications.filter(app => 
      app.status === 'phase4_accepted' || 
      app.status === 'auto_approved' ||
      app.status === 'phase3_approved'
    ).length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="mt-2 text-gray-600">Manage VB Accelerator applications and cohorts</p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { key: 'candidates', label: 'Applications (CRM)', count: applications.length },
              { key: 'cohorts', label: 'Cohort Management', count: cohorts.length },
              { key: 'interviewers', label: 'Interviewers', count: interviewers.length },
              { key: 'admin', label: 'Admin Tools' },
              { key: 'emails', label: 'Email Management' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                {tab.count !== undefined && (
                  <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Unified Candidate Profile Modal */}
      {selectedCandidateId && (
        <CandidateProfile
          candidateId={selectedCandidateId}
          onClose={() => setSelectedCandidateId(null)}
        />
      )}

      {/* Applications Tab */}
      {activeTab === 'candidates' && (
        <>
          {/* Cohort Selector */}
          <div className="bg-white rounded-lg shadow mb-8 p-6">
            <h2 className="text-xl font-semibold mb-4">Select Cohort</h2>
            <select
              value={selectedCohort}
              onChange={(e) => setSelectedCohort(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Cohorts</option>
              {cohorts.map(cohort => (
                <option key={cohort.id} value={cohort.id}>
                  {cohort.name} ({cohort.status})
                </option>
              ))}
            </select>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-600">Total Applications</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-2xl font-bold text-yellow-600">{stats.phase1}</div>
              <div className="text-sm text-gray-600">Phase 1</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-2xl font-bold text-orange-600">{stats.phase2}</div>
              <div className="text-sm text-gray-600">Phase 2</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-2xl font-bold text-blue-600">{stats.phase3}</div>
              <div className="text-sm text-gray-600">Phase 3</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-2xl font-bold text-purple-600">{stats.phase4}</div>
              <div className="text-sm text-gray-600">Phase 4</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-2xl font-bold text-green-600">{stats.accepted}</div>
              <div className="text-sm text-gray-600">Accepted</div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-lg shadow mb-8 p-6">
            <h2 className="text-xl font-semibold mb-4">Search Applicants</h2>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, or company..."
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Filter Controls */}
          <div className="bg-white rounded-lg shadow mb-8 p-6">
            <h2 className="text-xl font-semibold mb-4">Filter Applications</h2>
            
            {/* Phase Filters */}
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">By Phase:</h3>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'all', label: 'All Phases' },
                  { value: 'phase1', label: 'Phase 1 - Application' },
                  { value: 'phase2', label: 'Phase 2 - Group Session' },
                  { value: 'phase3', label: 'Phase 3 - In-Depth' },
                  { value: 'phase4', label: 'Phase 4 - Interview' },
                  { value: 'accepted', label: 'Final Accepted' }
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => setPhaseFilter(value)}
                    className={`px-4 py-2 rounded-md text-sm ${
                      phaseFilter === value 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Status Filters - Only show for Phase 1 and 3 */}
            {(phaseFilter === 'phase1' || phaseFilter === 'phase3') && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">By Status:</h3>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'all', label: 'All' },
                    { value: 'auto_accepted', label: 'Auto Accepted' },
                    { value: 'manually_approved', label: 'Manually Approved' },
                    { value: 'pending_manual_approval', label: 'Pending Manual Approval' },
                    { value: 'denied', label: 'Denied' }
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setFilter(value)}
                      className={`px-4 py-2 rounded-md text-sm ${
                        filter === value 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Unified Applicant Tracker Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold">Applicant Tracker ({applications.length})</h2>
              <p className="text-sm text-gray-600 mt-1">Unified view of all applicants across phases</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Applicant ID & Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Flags
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {applications.map((app) => (
                    <tr key={app.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {app.firstName} {app.lastName}
                          </div>
                          <div className="text-xs text-gray-500">ID: {app.id.slice(0, 8)}...</div>
                          <div className="text-xs text-gray-500">{app.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{app.companyName}</div>
                        <div className="text-sm text-gray-500">{app.role}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">Phase {app.currentPhase || 1}</span>
                          <div className={`w-2 h-2 rounded-full ${
                            (app.currentPhase || 1) >= 4 ? 'bg-green-500' :
                            (app.currentPhase || 1) >= 3 ? 'bg-blue-500' :
                            (app.currentPhase || 1) >= 2 ? 'bg-yellow-500' :
                            'bg-gray-400'
                          }`}></div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {(app.currentPhase || 1) === 1 && 'Initial Application'}
                          {app.currentPhase === 2 && 'Group Session'}
                          {app.currentPhase === 3 && 'In-Depth Application'}
                          {app.currentPhase === 4 && 'Interview'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(app.status)}`}>
                          {getUnifiedStatus(app)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="flex flex-wrap gap-1">
                            {getFlagIndicators(app)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => setSelectedCandidateId(app.id)}
                          className="text-blue-600 hover:text-blue-900 font-medium"
                        >
                          View Profile
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Email Management Tab */}
      {activeTab === 'emails' && (
        <div className="bg-white rounded-lg shadow mb-8 p-6">
          <h2 className="text-xl font-semibold mb-6">Email Management</h2>
          
          <div className="space-y-4">
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Send Approval Emails:</strong> Sends emails to all candidates marked as approved who haven't received emails yet.
                <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                  {applications.filter(app => app.emailQueue === true && !app.emailSent).length} queued
                </span>
              </p>
              <p className="text-sm text-gray-600">
                <strong>Send Rejection Emails:</strong> Sends emails to all candidates marked as rejected who haven't received emails yet.
                <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 rounded text-xs">
                  {applications.filter(app => (app.status === 'auto_rejected' || app.status === 'rejected_manual') && !app.emailSent).length} queued
                </span>
              </p>
              <p className="text-sm text-gray-600">
                <strong>Send Phase 4 Approval Emails:</strong> Sends emails to all Phase 4 candidates who haven't received emails yet.
                <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                  {applications.filter(app => app.status === 'phase4_assigned' && !app.emailSent).length} queued
                </span>
              </p>
              <p className="text-sm text-gray-600">
                <strong>Send Phase 4 Rejection Emails:</strong> Sends emails to all Phase 3 rejected candidates who haven't received emails yet.
                <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">
                  {applications.filter(app => app.status === 'phase3_rejected' && !app.emailSent).length} queued
                </span>
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={sendApprovalEmails}
                disabled={emailLoading}
                className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {emailLoading ? 'Sending...' : 'Send P1→P2 Approval Emails'}
              </button>
              
              <button
                onClick={sendRejectionEmails}
                disabled={emailLoading}
                className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {emailLoading ? 'Sending...' : 'Send P1 Rejection Emails'}
              </button>

              <button
                onClick={sendPhase4ApprovalEmails}
                disabled={emailLoading}
                className="bg-purple-600 text-white px-6 py-2 rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                {emailLoading ? 'Sending...' : 'Send P4 Approval Emails'}
              </button>
              
              <button
                onClick={sendPhase4RejectionEmails}
                disabled={emailLoading}
                className="bg-orange-600 text-white px-6 py-2 rounded-md hover:bg-orange-700 disabled:opacity-50"
              >
                {emailLoading ? 'Sending...' : 'Send P4 Rejection Emails'}
              </button>
            </div>
            
            {emailResult && (
              <div className={`p-4 rounded-md ${
                emailResult.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}>
                {emailResult.message}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin Tools Tab */}
      {activeTab === 'admin' && (
        <div className="bg-white rounded-lg shadow mb-8 p-6">
          <h2 className="text-xl font-semibold mb-6">Admin Management</h2>
          
          {/* Create New Admin User */}
          <div className="mb-8 p-4 border border-gray-200 rounded-lg">
            <h3 className="text-lg font-medium mb-4">Create New Admin User</h3>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <input
                type="text"
                value={newAdminData.firstName}
                onChange={(e) => setNewAdminData({...newAdminData, firstName: e.target.value})}
                placeholder="First Name"
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                value={newAdminData.lastName}
                onChange={(e) => setNewAdminData({...newAdminData, lastName: e.target.value})}
                placeholder="Last Name"
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <input
                type="email"
                value={newAdminData.email}
                onChange={(e) => setNewAdminData({...newAdminData, email: e.target.value})}
                placeholder="Email Address"
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="password"
                value={newAdminData.password}
                onChange={(e) => setNewAdminData({...newAdminData, password: e.target.value})}
                placeholder="Password (min 6 chars)"
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <button
              onClick={createAdminUser}
              disabled={createAdminLoading}
              className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              {createAdminLoading ? 'Creating...' : 'Create Admin User'}
            </button>
          </div>
          
          {adminResult && (
            <div className={`p-4 rounded-md ${
              adminResult.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              {adminResult.message}
            </div>
          )}
        </div>
      )}

      {/* Cohorts Tab */}
      {activeTab === 'cohorts' && (
        <div className="space-y-8">
          {/* Create New Cohort */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-6">Create New Cohort</h2>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              createCohort();
            }} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cohort Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newCohort.name}
                    onChange={(e) => setNewCohort({...newCohort, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Cohort Fall 2024"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Program Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={newCohort.programStart}
                    onChange={(e) => setNewCohort({...newCohort, programStart: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phase 1 Application Start <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={newCohort.phase1Start}
                    onChange={(e) => setNewCohort({...newCohort, phase1Start: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phase 1 Application Deadline <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={newCohort.phase1Deadline}
                    onChange={(e) => setNewCohort({...newCohort, phase1Deadline: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phase 3 Application Start <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={newCohort.phase3Start}
                    onChange={(e) => setNewCohort({...newCohort, phase3Start: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phase 3 Application Deadline <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={newCohort.phase3Deadline}
                    onChange={(e) => setNewCohort({...newCohort, phase3Deadline: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={cohortLoading}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {cohortLoading ? 'Creating...' : 'Create Cohort'}
                </button>
              </div>
            </form>
          </div>

          {/* Existing Cohorts */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-6">Existing Cohorts</h2>
            
            {cohorts.length === 0 ? (
              <p className="text-gray-500">No cohorts created yet.</p>
            ) : (
              <div className="space-y-4">
                {cohorts.map((cohort) => (
                  <div key={cohort.id} className="border border-gray-200 rounded-lg p-4">
                    {editingCohort === cohort.id ? (
                      // Edit Mode
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Cohort Name
                            </label>
                            <input
                              type="text"
                              value={editCohortData.name || ''}
                              onChange={(e) => setEditCohortData({...editCohortData, name: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Status
                            </label>
                            <select
                              value={editCohortData.status || 'draft'}
                              onChange={(e) => setEditCohortData({...editCohortData, status: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="draft">Draft</option>
                              <option value="open_applications">Open for Applications</option>
                              <option value="closed_applications">Applications Closed</option>
                              <option value="in_progress">Program In Progress</option>
                              <option value="completed">Completed</option>
                            </select>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Phase 1 Start
                            </label>
                            <input
                              type="date"
                              value={editCohortData.timeline?.phase1Start || ''}
                              onChange={(e) => setEditCohortData({
                                ...editCohortData, 
                                timeline: {...editCohortData.timeline, phase1Start: e.target.value}
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Phase 1 Deadline
                            </label>
                            <input
                              type="date"
                              value={editCohortData.timeline?.phase1Deadline || ''}
                              onChange={(e) => setEditCohortData({
                                ...editCohortData, 
                                timeline: {...editCohortData.timeline, phase1Deadline: e.target.value}
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Phase 3 Start
                            </label>
                            <input
                              type="date"
                              value={editCohortData.timeline?.phase3Start || ''}
                              onChange={(e) => setEditCohortData({
                                ...editCohortData, 
                                timeline: {...editCohortData.timeline, phase3Start: e.target.value}
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Phase 3 Deadline
                            </label>
                            <input
                              type="date"
                              value={editCohortData.timeline?.phase3Deadline || ''}
                              onChange={(e) => setEditCohortData({
                                ...editCohortData, 
                                timeline: {...editCohortData.timeline, phase3Deadline: e.target.value}
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>

                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => {
                              setEditingCohort(null);
                              setEditCohortData({});
                            }}
                            className="px-4 py-2 text-gray-600 hover:text-gray-800"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => saveCohortEdit(cohort.id)}
                            disabled={cohortLoading}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                          >
                            {cohortLoading ? 'Saving...' : 'Save Changes'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div>
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-lg font-medium text-gray-900">{cohort.name}</h3>
                            <div className="flex items-center space-x-4 mt-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                cohort.status === 'open_applications' ? 'bg-green-100 text-green-800' :
                                cohort.status === 'closed_applications' ? 'bg-yellow-100 text-yellow-800' :
                                cohort.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                cohort.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {cohort.status?.replace('_', ' ').toUpperCase()}
                              </span>
                              <span className="text-sm text-gray-500">
                                Created: {cohort.createdAt?.toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setEditingCohort(cohort.id);
                                setEditCohortData({
                                  name: cohort.name,
                                  status: cohort.status,
                                  timeline: cohort.timeline || {}
                                });
                              }}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => deleteCohort(cohort.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">Phase 1 Applications:</span>
                            <div className="text-gray-600">
                              {cohort.timeline?.phase1Start ? new Date(cohort.timeline.phase1Start).toLocaleDateString() : 'Not set'} - 
                              {cohort.timeline?.phase1Deadline ? new Date(cohort.timeline.phase1Deadline).toLocaleDateString() : 'Not set'}
                            </div>
                          </div>
                          
                          <div>
                            <span className="font-medium text-gray-700">Phase 3 Applications:</span>
                            <div className="text-gray-600">
                              {cohort.timeline?.phase3Start ? new Date(cohort.timeline.phase3Start).toLocaleDateString() : 'Not set'} - 
                              {cohort.timeline?.phase3Deadline ? new Date(cohort.timeline.phase3Deadline).toLocaleDateString() : 'Not set'}
                            </div>
                          </div>
                          
                          <div>
                            <span className="font-medium text-gray-700">Program Start:</span>
                            <div className="text-gray-600">
                              {cohort.timeline?.programStart ? new Date(cohort.timeline.programStart).toLocaleDateString() : 'Not set'}
                            </div>
                          </div>
                        </div>

                        {/* Phase 2 Sessions */}
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="font-medium text-gray-900">Phase 2 Group Sessions</h4>
                            <button
                              onClick={() => {
                                setAddingSessionToCohort(cohort.id);
                                setNewSession({ date: '', time: '', zoomLink: '' });
                              }}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              + Add Session
                            </button>
                          </div>
                          
                          {/* Add Session Form */}
                          {addingSessionToCohort === cohort.id && (
                            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                              <h5 className="font-medium text-blue-900 mb-3">Add New Session</h5>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Date <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="date"
                                    value={newSession.date}
                                    onChange={(e) => setNewSession({...newSession, date: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Time <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="time"
                                    value={newSession.time}
                                    onChange={(e) => setNewSession({...newSession, time: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Zoom Link <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="url"
                                    value={newSession.zoomLink}
                                    onChange={(e) => setNewSession({...newSession, zoomLink: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="https://zoom.us/j/..."
                                    required
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => {
                                    setAddingSessionToCohort(null);
                                    setNewSession({ date: '', time: '', zoomLink: '' });
                                  }}
                                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => addSession(cohort.id)}
                                  disabled={cohortLoading}
                                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >
                                  {cohortLoading ? 'Adding...' : 'Add Session'}
                                </button>
                              </div>
                            </div>
                          )}
                          
                          {cohort.phase2Sessions && cohort.phase2Sessions.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {cohort.phase2Sessions.map((session, index) => {
                                console.log('Rendering session:', session);
                                const attendees = getSessionAttendees(session.id, cohort.id);
                                const attendedCount = attendees.filter(app => app.verifiedSessions?.includes(session.id)).length;
                                const isExpanded = expandedSessions.has(session.id);
                                
                                return (
                                <div key={session.id || index} className="bg-gray-50 p-3 rounded border">
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="font-medium text-sm">
                                        {new Date(`${session.date} ${session.time}`).toLocaleDateString()} at {session.time}
                                      </div>
                                      <div className="text-xs text-gray-600 mt-1">
                                        Code: <span className="font-mono bg-white px-1 rounded">{session.code || 'No code'}</span>
                                      </div>
                                      {session.zoomLink && (
                                        <div className="text-xs text-blue-600 mt-1">
                                          <a href={session.zoomLink} target="_blank" rel="noopener noreferrer">
                                            Zoom Link
                                          </a>
                                        </div>
                                      )}
                                      
                                      {/* Attendee Summary */}
                                      <div className="flex items-center space-x-3 mt-2 text-xs">
                                        <span className="text-blue-600">
                                          <i className="fas fa-users mr-1"></i>Signed Up: {attendees.length}
                                        </span>
                                        <span className="text-green-600">
                                          <i className="fas fa-check mr-1"></i>Attended: {attendedCount}
                                        </span>
                                        {attendees.length > 0 && (
                                          <button
                                            onClick={() => toggleSessionExpansion(session.id)}
                                            className="text-purple-600 hover:text-purple-800"
                                          >
                                            <i className={`fas ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} mr-1`}></i>
                                            {isExpanded ? 'Hide Details' : 'Show Details'}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => removeSession(cohort.id, session.id)}
                                      className="text-red-600 hover:text-red-800 text-xs ml-2"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  
                                  {/* Expandable Attendee Details */}
                                  {isExpanded && attendees.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                      <div className="text-xs font-medium text-gray-700 mb-2">
                                        Attendee Details:
                                      </div>
                                      <div className="space-y-1">
                                        {attendees.map(attendee => {
                                          const hasAttended = attendee.verifiedSessions?.includes(session.id);
                                          return (
                                            <div key={attendee.id} className="flex items-center justify-between text-xs">
                                              <div className="flex items-center space-x-2">
                                                <i className={`fas ${hasAttended ? 'fa-check-circle text-green-600' : 'fa-times-circle text-red-600'}`}></i>
                                                <span className="font-medium">
                                                  {attendee.firstName} {attendee.lastName}
                                                </span>
                                                <span className="text-gray-500">
                                                  ({attendee.email})
                                                </span>
                                              </div>
                                              <span className="text-gray-500">
                                                {hasAttended ? 'Attended' : 'No show'}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-sm">No sessions scheduled</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Interviewers Tab */}
      {activeTab === 'interviewers' && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Interviewer Management</h2>
            
            {/* Add New Interviewer */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Add New Interviewer</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={newInterviewer.name}
                    onChange={(e) => setNewInterviewer({...newInterviewer, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={newInterviewer.email}
                    onChange={(e) => setNewInterviewer({...newInterviewer, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={newInterviewer.title}
                    onChange={(e) => setNewInterviewer({...newInterviewer, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Senior Partner, Investment Manager, etc."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Calendar Link</label>
                  <input
                    type="url"
                    value={newInterviewer.calendarLink}
                    onChange={(e) => setNewInterviewer({...newInterviewer, calendarLink: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://calendly.com/interviewer"
                  />
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={addInterviewer}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Add Interviewer
                </button>
              </div>
            </div>

            {/* Interviewers List */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Current Interviewers ({interviewers.length})</h3>
              {interviewers.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No interviewers added yet</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {interviewers.map((interviewer) => (
                    <div key={interviewer.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{interviewer.name}</h4>
                          {interviewer.title && (
                            <p className="text-sm text-gray-600">{interviewer.title}</p>
                          )}
                        </div>
                        <button
                          onClick={() => removeInterviewer(interviewer.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Email:</span> {interviewer.email}
                        </p>
                        {interviewer.calendarLink && (
                          <p className="text-sm">
                            <a 
                              href={interviewer.calendarLink} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800"
                            >
                              📅 Calendar Link
                            </a>
                          </p>
                        )}
                      </div>
                      {interviewer.createdAt && (
                        <p className="text-xs text-gray-400 mt-2">
                          Added {interviewer.createdAt.toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;